<template>
  <div
    v-if="data"
    class="w-full"
  >
    <div class="bg-white dark:bg-surface-100 rounded-lg shadow p-6 w-full max-w-full overflow-hidden">
      <!-- Header with back button and title (contact-style) -->
      <div class="flex justify-between items-center px-2 py-4 mb-6 bg-gray-50 dark:bg-surface-200 border-b rounded-lg">
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-arrow-left"
            class="p-button-text"
            @click="goBack"
          />
          <h2 class="text-xl font-semibold text-surface-900 dark:text-surface-100">
            {{ data.name }}
          </h2>
        </div>
      </div>

      <!-- Wiki item sub-tabs (Overview, Prep, DePrep, Guides, Finance) -->
      <div class="w-full">
        <Tabs
          :value="tabValue"
          class="w-full"
        >
          <TabList class="w-full flex border-b">
            <Tab value="overview">
              <router-link
                v-slot="{ href, navigate }"
                :to="`/wiki/item/${route.params.id}/overview`"
                custom
              >
                <a
                  v-ripple
                  :href="href"
                  class="flex items-center gap-2 text-inherit"
                  @click="navigate"
                >
                  <i class="pi pi-home" />
                  <span>Overview</span>
                </a>
              </router-link>
            </Tab>
            <Tab value="prep">
              <router-link
                v-slot="{ href, navigate }"
                :to="`/wiki/item/${route.params.id}/prep`"
                custom
              >
                <a
                  v-ripple
                  :href="href"
                  class="flex items-center gap-2 text-inherit"
                  @click="navigate"
                >
                  <i class="pi pi-box" />
                  <span>Prep</span>
                </a>
              </router-link>
            </Tab>
            <Tab value="deprep">
              <router-link
                v-slot="{ href, navigate }"
                :to="`/wiki/item/${route.params.id}/deprep`"
                custom
              >
                <a
                  v-ripple
                  :href="href"
                  class="flex items-center gap-2 text-inherit"
                  @click="navigate"
                >
                  <i class="pi pi-inbox" />
                  <span>DePrep</span>
                </a>
              </router-link>
            </Tab>
            <Tab value="guides">
              <router-link
                v-slot="{ href, navigate }"
                :to="`/wiki/item/${route.params.id}/guides`"
                custom
              >
                <a
                  v-ripple
                  :href="href"
                  class="flex items-center gap-2 text-inherit"
                  @click="navigate"
                >
                  <i class="pi pi-book" />
                  <span>Guides</span>
                </a>
              </router-link>
            </Tab>
          </TabList>
          <TabPanels class="w-full py-6">
            <router-view v-slot="{ Component }">
              <keep-alive>
                <component
                  :is="Component"
                  :item="data"
                />
              </keep-alive>
            </router-view>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </div>
  <div
    v-else-if="loadError"
    class="p-4 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg"
  >
    {{ loadError }}
  </div>
  <div
    v-else
    class="w-full"
  >
    <div class="bg-white dark:bg-surface-100 rounded-lg shadow p-6 w-full max-w-full overflow-hidden">
      <div class="flex justify-between items-center px-2 py-4 mb-6 bg-gray-50 dark:bg-surface-200 border-b rounded-lg">
        <div class="flex items-center gap-2">
          <Button
            icon="pi pi-arrow-left"
            class="p-button-text"
            @click="goBack"
          />
          <h2 class="text-xl font-semibold text-surface-900 dark:text-surface-100">
            Loading...
          </h2>
        </div>
      </div>
      <div class="flex justify-center items-center py-12">
        <ProgressSpinner />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, provide } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getWikiItemById, resolveWikiImageUrl } from '@/services/wikiService';

const router = useRouter();
const route = useRoute();
const data = ref(null);
const loadError = ref(null);
const loading = ref(true);

// Current tab value from route (last path segment), e.g. overview, prep, deprep, guides
const tabValue = computed(() => {
  const segments = route.path.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  return last && ['overview', 'prep', 'deprep', 'guides'].includes(last) ? last : 'overview';
});

const goBack = () => {
  router.push('/wiki/items');
};

async function loadWikiItem() {
  const itemId = route.params.id;
  if (!itemId) return;
  loading.value = true;
  loadError.value = null;
  try {
    const res = await getWikiItemById(itemId);
    const raw = res?.data;
    if (!raw) {
      loadError.value = 'Item not found';
      return;
    }
    const imageUrl = resolveWikiImageUrl(raw.image_url) || null;
    if (raw.images?.length && raw.images[0]) {
      raw.images[0].image = imageUrl || raw.images[0].image || null;
    } else {
      raw.images = [{ id: 1, image: imageUrl, isDefault: true }];
    }
    data.value = raw;
  } catch (e) {
    loadError.value = e?.response?.data?.message || e?.message || 'Failed to load wiki item';
  } finally {
    loading.value = false;
  }
}

provide('refreshWikiItem', loadWikiItem);

onMounted(() => {
  loadWikiItem();
});
</script>

<style scoped>
:deep(.p-tabview-panels) {
  width: 100% !important;
}

:deep(.p-tabview-panel) {
  width: 100% !important;
}
</style>
